<template>
  <div class="location-stock-check-container">
    <header class="header">
      <button @click="handleBack" class="back-btn" aria-label="返回">
        <img src="@/assets/返回.svg" alt="返回" class="icon-back" />
      </button>
      <h1 class="title">库位盘点</h1>
      <button @click="handleAddCheck" class="btn add-btn">新增</button>
    </header>

    <main class="content">
      <div class="form-section source-section">
        <div class="top-filter">
          <div
            :class="['filter-item', { active: checkStatusFilter === 0 }]"
            @click="setCheckStatusFilter(0)"
          >
            待盘点
          </div>
          <div
            :class="['filter-item', { active: checkStatusFilter === 1 }]"
            @click="setCheckStatusFilter(1)"
          >
            通过
          </div>
          <div
            :class="['filter-item', { active: checkStatusFilter === 2 }]"
            @click="setCheckStatusFilter(2)"
          >
            不通过
          </div>
        </div>

        <div class="list-search">
          <input
            v-model="listCombinedSearchText"
            class="list-search-input"
            placeholder="库位 供应商（空格分割；只搜供应商：空格+供应商）"
          />
          <button
            v-if="listCombinedSearchText"
            class="list-search-clear"
            @click="clearListSearch"
            aria-label="清空搜索"
          >
            ×
          </button>
        </div>

        <div v-if="statusMsg" class="status-banner" :class="statusType">
          {{ statusMsg }}
        </div>

        <div v-if="loading" class="loading-state">
          <div class="loading-spinner"></div>
          <p class="loading-text">加载中...</p>
        </div>

        <div v-if="errorMsg" class="error-message">{{ errorMsg }}</div>

        <div
          v-if="checkList.length === 0 && !loading && !errorMsg"
          class="empty-state"
        >
          <div class="empty-icon">📄</div>
          <p class="empty-text">暂无盘点订单</p>
          <button @click="handleAddCheck" class="btn empty-action-btn">
            立即创建
          </button>
        </div>

        <div class="card-list" v-else-if="!loading && !errorMsg">
          <div
            :class="['data-card', getStatusClass(item.checkStatus)]"
            v-for="item in checkList"
            :key="item.id"
            @click="navigateToDetail(item.id)"
          >
            <div class="card-header">
              <span class="card-label">盘点单号 {{ item.inventeNo }}</span>
              <span
                class="card-status"
                :class="getStatusClass(item.checkStatus)"
                >{{ getStatusText(item.checkStatus) }}</span
              >
            </div>
            <div class="card-body">
              <div class="card-row">
                <span class="row-label">创建时间：</span>
                <span class="row-value">{{ formatDate(item.createTime) }}</span>
              </div>
              <div class="card-row">
                <span class="row-label">库位：</span>
                <span class="row-value">{{ item.wareName || "-" }}</span>
              </div>
              <div class="card-row" v-if="item.checkStatus === 2">
                <span class="row-label">不通过原因：</span>
                <span class="row-value">{{ item.reason }}</span>
              </div>
            </div>
            <div class="card-footer">
              <button
                @click.stop="handleEnterCheck(item.id)"
                @touchend.stop.prevent="handleEnterCheck(item.id)"
                class="btn enter-check-btn"
                :disabled="item.status === 2 || item.status === 3"
              >
                {{
                  item.checkStatus === 0
                    ? "开始盘点"
                    : item.checkStatus === 2
                    ? "审核未通过"
                    : "审核通过"
                }}
              </button>

              <button
                @click.stop="handleDeleteCheck(item.id)"
                @touchend.stop.prevent="handleDeleteCheck(item.id)"
                class="btn delete-btn"
                :disabled="deletingId === item.id"
              >
                {{ deletingId === item.id ? "删除中..." : "删除" }}
              </button>
            </div>
          </div>
        </div>

        <div
          class="pagination-container"
          v-if="total > 0 && !loading && !errorMsg"
        >
          <div class="pagination-info">
            共 {{ total }} 条记录，第 {{ current }} / {{ totalPages }} 页
          </div>
          <div class="pagination-controls">
            <button
              class="page-btn"
              :disabled="current === 1 || loading"
              @click="handlePageChange(current - 1)"
            >
              上一页
            </button>
            <button
              class="page-btn"
              :disabled="current >= totalPages || loading"
              @click="handlePageChange(current + 1)"
            >
              下一页
            </button>
          </div>
        </div>
      </div>
    </main>

    <div v-if="showAddModal" class="modal-overlay">
      <div class="modal-container">
        <div class="modal-header">
          <h3 class="modal-title">新增库位盘点单</h3>
          <button @click="resetModal" class="modal-close" aria-label="关闭">
            ×
          </button>
        </div>

        <div v-if="statusMsg" class="status-banner" :class="statusType">
          {{ statusMsg }}
        </div>

        <div class="modal-body">
          <div class="form-group">
            <label>扫描/输入库位码</label>
            <div class="search-input-wrapper">
              <input
                type="text"
                v-model="locationCode"
                placeholder="请扫描或输入库位码"
                class="search-input"
                @input="handleLocationScan"
              />
            </div>
            <div v-if="locationScanLoading" class="loading-state small-loading">
              <div class="loading-spinner"></div>
            </div>
            <div v-if="locationScanError" class="error-message">
              {{ locationScanError }}
            </div>

            <div v-if="locationCandidates.length > 1" class="location-dropdown">
              <div
                v-for="location in locationCandidates"
                :key="location.id"
                class="location-dropdown-item"
                @click="selectLocation(location)"
              >
                {{ location.wareName || location.wareNo }}
              </div>
            </div>
          </div>

          <div v-if="scannedLocation" class="location-info-card">
            <div class="location-info-header">
              <span class="location-name">{{
                scannedLocation.wareName || scannedLocation.wareNo
              }}</span>
            </div>
          </div>

          <div v-if="scannedLocation" class="form-group">
            <div class="material-list-header">
              <label>库位物料列表</label>
              <button
                v-if="locationMaterials.length > 0"
                @click="toggleSelectAllMaterials"
                class="select-all-btn"
              >
                {{
                  selectedMaterials.length === locationMaterials.length
                    ? "取消全选"
                    : "全选"
                }}
              </button>
            </div>
            <div
              v-if="locationMaterialsLoading"
              class="loading-state small-loading"
            >
              <div class="loading-spinner"></div>
            </div>
            <div
              v-else-if="locationMaterials.length === 0"
              class="empty-list-tip"
            >
              该库位暂无物料
            </div>
            <div v-else class="material-list">
              <div
                v-for="material in locationMaterials"
                :key="material.id || material.materialNo"
                class="material-item"
                :class="{ selected: isMaterialSelected(material) }"
                @click="toggleMaterialSelect(material)"
              >
                <div class="material-checkbox">
                  <div
                    class="checkbox"
                    :class="{ checked: isMaterialSelected(material) }"
                  >
                    <span v-if="isMaterialSelected(material)">✓</span>
                  </div>
                </div>
                <div class="material-info">
                  <div class="material-no">{{ material.materialNo }}</div>
                  <div class="material-name">{{ material.materialName }}</div>
                  <div class="material-supplier" v-if="material.supplierName">
                    {{ material.supplierName }}
                  </div>
                </div>
                <div class="material-right">
                  <div
                    :class="[
                      'material-status-badge',
                      material.isBad === 1 ? 'status-bad' : 'status-good',
                    ]"
                  >
                    {{ material.isBad === 1 ? "不良品" : "良品" }}
                  </div>
                  <div class="material-qty">
                    {{ formatQty(material.stockNum)
                    }}{{ material.packageUnitName || "" }}
                  </div>
                </div>
              </div>
            </div>

            <div
              v-if="totalMaterials > locationMaterials.length"
              class="load-more-container"
            >
              <button
                @click="loadMoreMaterials"
                class="load-more-btn"
                :disabled="locationMaterialsLoading"
              >
                {{ locationMaterialsLoading ? "加载中..." : "加载更多" }}
              </button>
            </div>

            <div v-if="selectedMaterials.length > 0" class="selected-count">
              已选择 {{ selectedMaterials.length }} 个物料
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button @click="resetModal" class="btn modal-cancel">取消</button>
          <button
            @click="createCheckOrder"
            class="btn modal-confirm"
            :disabled="createLoading || !scannedLocation"
          >
            {{ createLoading ? "创建中..." : "创建盘点单" }}
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="showSupplierNameModal"
      class="modal-overlay"
      @click.self="closeSupplierNameModal"
    >
      <div class="modal-container supplier-name-modal-container">
        <div class="modal-header">
          <h3 class="modal-title">{{ supplierNameModalTitle }}</h3>
          <button
            @click="closeSupplierNameModal"
            class="modal-close"
            aria-label="关闭"
          >
            ×
          </button>
        </div>
        <div class="modal-body">
          <div
            v-if="supplierNameModalItems.length === 0"
            class="empty-list-tip"
          >
            暂无供应商
          </div>
          <div v-else class="supplier-name-modal-list">
            <div
              v-for="(name, idx) in supplierNameModalItems"
              :key="`${idx}-${name}`"
              class="supplier-name-modal-item"
            >
              {{ name }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import {
  getStockCheckHistory,
  deleteStockCheck,
  warehouseSaveStockCheck,
} from "@/api/stockCheck";
import { getWarePage, getPalletDetail } from "@/api/stockTransfer";

export default {
  name: "LocationStockCheck",
  setup() {
    const router = useRouter();

    const checkList = ref([]);
    const loading = ref(false);
    const errorMsg = ref("");
    const statusMsg = ref("");
    const statusType = ref("success");
    const showAddModal = ref(false);
    const createLoading = ref(false);

    const current = ref(1);
    const size = ref(10);
    const total = ref(0);

    const checkStatusFilter = ref(0);

    const listCombinedSearchText = ref("");
    const listSearchWareName = ref("");
    const listSearchSupplierName = ref("");

    const showSupplierNameModal = ref(false);
    const supplierNameModalTitle = ref("供应商");
    const supplierNameModalItems = ref([]);

    const deletingId = ref("");

    const totalPages = computed(() => {
      const pages = Math.ceil((total.value || 0) / (size.value || 1));
      return pages > 0 ? pages : 1;
    });

    const locationCode = ref("");
    const locationScanLoading = ref(false);
    const locationScanError = ref("");
    const scannedLocation = ref(null);
    const locationMaterials = ref([]);
    const locationMaterialsLoading = ref(false);
    const locationCandidates = ref([]);
    const selectedMaterials = ref([]);
    const materialCurrentPage = ref(1);
    const materialPageSize = ref(20);
    const totalMaterials = ref(0);

    const fetchCheckHistory = async (page = 1) => {
      try {
        loading.value = true;
        errorMsg.value = "";

        current.value = page;

        const params = {
          current: page,
          size: size.value,
          checkStatus: checkStatusFilter.value,
          inventeType: 1,
        };

        if (listSearchWareName.value) {
          params.wareName = listSearchWareName.value;
        }
        if (listSearchSupplierName.value) {
          params.supplierName = listSearchSupplierName.value;
        }

        const res = await getStockCheckHistory(params);
        const data = res?.data || {};

        checkList.value = data.records || [];
        total.value = data.total || 0;

        if (checkList.value.length === 0 && !listCombinedSearchText.value) {
          statusMsg.value = '暂无盘点记录，点击"新增"开始创建';
          statusType.value = "info";
        } else {
          statusMsg.value = "";
        }
      } catch (err) {
        errorMsg.value = "获取盘点历史失败，请稍后重试";
        console.error(err);
      } finally {
        loading.value = false;
      }
    };

    const handleLocationScan = async () => {
      const code = String(locationCode.value || "").trim();
      if (!code) {
        locationScanError.value = "";
        scannedLocation.value = null;
        locationMaterials.value = [];
        return;
      }

      locationScanLoading.value = true;
      locationScanError.value = "";
      try {
        const res = await getWarePage({
          wareNo: code,
          wareType: 2,
          inventeType: 1,
        });
        const data = res?.data || {};
        const records = Array.isArray(data.records) ? data.records : [];

        if (records.length === 0) {
          locationScanError.value = "未找到匹配的库位";
          scannedLocation.value = null;
          locationMaterials.value = [];
        } else if (records.length === 1) {
          scannedLocation.value = records[0];
          locationScanError.value = "";
          locationCandidates.value = [];
          await fetchLocationMaterials(records[0]);
        } else {
          locationCandidates.value = records;
          scannedLocation.value = null;
          locationMaterials.value = [];
        }
      } catch (e) {
        locationScanError.value = "库位查询失败";
        scannedLocation.value = null;
        locationMaterials.value = [];
      } finally {
        locationScanLoading.value = false;
      }
    };

    const fetchLocationMaterials = async (location, page = 1) => {
      locationMaterialsLoading.value = true;
      try {
        const res = await getPalletDetail({
          palletCode: location.wareNo || location.wareName,
          current: page,
          size: materialPageSize.value,
        });
        const data = res?.data || {};
        const records = Array.isArray(data.records) ? data.records : [];
        totalMaterials.value = data.total || 0;

        let allMaterials = [];
        records.forEach((record) => {
          if (Array.isArray(record.materialList)) {
            allMaterials = allMaterials.concat(record.materialList);
          }
        });

        if (page === 1) {
          locationMaterials.value = allMaterials;
        } else {
          locationMaterials.value = [
            ...locationMaterials.value,
            ...allMaterials,
          ];
        }
      } catch (e) {
        console.error("获取库位物料失败", e);
        locationMaterials.value = [];
      } finally {
        locationMaterialsLoading.value = false;
      }
    };

    const selectLocation = async (location) => {
      scannedLocation.value = location;
      locationCandidates.value = [];
      locationScanError.value = "";
      selectedMaterials.value = [];
      materialCurrentPage.value = 1;
      totalMaterials.value = 0;
      await fetchLocationMaterials(location, 1);
    };

    const toggleMaterialSelect = (material) => {
      const key = material.id || material.materialNo;
      const index = selectedMaterials.value.findIndex(
        (m) => (m.id || m.materialNo) === key,
      );
      if (index > -1) {
        selectedMaterials.value.splice(index, 1);
      } else {
        selectedMaterials.value.push(material);
      }
    };

    const toggleSelectAllMaterials = () => {
      if (selectedMaterials.value.length === locationMaterials.value.length) {
        selectedMaterials.value = [];
      } else {
        selectedMaterials.value = [...locationMaterials.value];
      }
    };

    const loadMoreMaterials = async () => {
      if (locationMaterialsLoading.value) return;
      const totalPages = Math.ceil(
        totalMaterials.value / materialPageSize.value,
      );
      if (materialCurrentPage.value >= totalPages) return;

      materialCurrentPage.value++;
      await fetchLocationMaterials(
        scannedLocation.value,
        materialCurrentPage.value,
      );
    };

    const isMaterialSelected = (material) => {
      const key = material.id || material.materialNo;
      return selectedMaterials.value.some(
        (m) => (m.id || m.materialNo) === key,
      );
    };

    const setCheckStatusFilter = (status) => {
      checkStatusFilter.value = status;
      current.value = 1;
      fetchCheckHistory(1);
    };

    const handlePageChange = (page) => {
      if (page < 1 || page > totalPages.value) return;
      fetchCheckHistory(page);
    };

    const clearListSearch = () => {
      listCombinedSearchText.value = "";
      listSearchWareName.value = "";
      listSearchSupplierName.value = "";
      current.value = 1;
      fetchCheckHistory(1);
    };

    const handleAddCheck = () => {
      locationCode.value = "";
      locationScanError.value = "";
      scannedLocation.value = null;
      locationMaterials.value = [];
      statusMsg.value = "";
      statusType.value = "success";
      showAddModal.value = true;
    };

    const resetModal = () => {
      showAddModal.value = false;
      locationCode.value = "";
      locationScanError.value = "";
      scannedLocation.value = null;
      locationMaterials.value = [];
      statusMsg.value = "";
      statusType.value = "success";
    };

    const createCheckOrder = async () => {
      if (!scannedLocation.value) {
        statusMsg.value = "请先扫描库位";
        statusType.value = "error";
        return;
      }

      createLoading.value = true;
      statusMsg.value = "";

      try {
        const hasSelectedMaterials = selectedMaterials.value.length > 0;

        const params = {
          wareId: scannedLocation.value.id,
          wareName:
            scannedLocation.value.wareName || scannedLocation.value.wareNo,
          selectFlag: hasSelectedMaterials ? 1 : 0,
          materialList: hasSelectedMaterials
            ? selectedMaterials.value.map((m) => ({
                materialId: m.id,
                stockNum: m.stockNum,
                isBad: m.isBad || 0,
              }))
            : [],
        };

        console.log("创建库位盘点单参数:", params);

        const res = await warehouseSaveStockCheck(params);

        if (res?.success) {
          statusMsg.value = "创建盘点单成功";
          statusType.value = "success";
          scannedLocation.value = null;
          locationMaterials.value = [];
          selectedMaterials.value = [];
          showAddModal.value = false;
          await fetchCheckHistory(1);
        } else {
          statusMsg.value = res?.msg || "创建盘点单失败";
          statusType.value = "error";
        }
      } catch (err) {
        statusMsg.value = "创建盘点单失败，请稍后重试";
        statusType.value = "error";
        console.error(err);
      } finally {
        createLoading.value = false;
      }
    };

    const handleEnterCheck = (id) => {
      router.push(`/mobile/location-stock-check/detail/${id}`);
    };

    const navigateToDetail = (id) => {
      router.push(`/mobile/location-stock-check/detail/${id}`);
    };

    const handleDeleteCheck = async (id) => {
      if (!confirm("确定要删除这条盘点单吗？")) return;

      deletingId.value = id;
      try {
        await deleteStockCheck(id);
        statusMsg.value = "删除成功";
        statusType.value = "success";
        fetchCheckHistory(current.value);
      } catch (err) {
        statusMsg.value = "删除失败，请稍后重试";
        statusType.value = "error";
        console.error(err);
      } finally {
        deletingId.value = "";
      }
    };

    const openSupplierNameModal = (item) => {
      if (!item.supplierName) return;
      supplierNameModalTitle.value = "供应商";
      supplierNameModalItems.value = item.supplierName
        ? [item.supplierName]
        : [];
      showSupplierNameModal.value = true;
    };

    const closeSupplierNameModal = () => {
      showSupplierNameModal.value = false;
    };

    const getStatusClass = (status) => {
      switch (status) {
        case 0:
          return "status-pending";
        case 1:
          return "status-completed";
        case 2:
          return "status-canceled";
        default:
          return "";
      }
    };

    const getStatusText = (status) => {
      switch (status) {
        case 0:
          return "待盘点";
        case 1:
          return "通过";
        case 2:
          return "不通过";
        default:
          return "";
      }
    };

    const formatDate = (dateStr) => {
      if (!dateStr) return "-";
      const date = new Date(dateStr);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      return `${year}-${month}-${day} ${hours}:${minutes}`;
    };

    const formatQty = (val) => {
      if (val === null || val === undefined || val === "") return "-";
      const n = Number(val);
      if (!Number.isFinite(n)) return String(val);
      return String(n);
    };

    const handleBack = () => router.back();

    onMounted(() => {
      fetchCheckHistory(1);
    });

    return {
      checkList,
      loading,
      errorMsg,
      statusMsg,
      statusType,
      showAddModal,
      createLoading,
      current,
      size,
      total,
      totalPages,
      checkStatusFilter,
      listCombinedSearchText,
      showSupplierNameModal,
      supplierNameModalTitle,
      supplierNameModalItems,
      deletingId,
      locationCode,
      locationScanLoading,
      locationScanError,
      scannedLocation,
      locationMaterials,
      locationMaterialsLoading,
      locationCandidates,
      setCheckStatusFilter,
      handlePageChange,
      clearListSearch,
      handleAddCheck,
      resetModal,
      createCheckOrder,
      handleEnterCheck,
      navigateToDetail,
      handleDeleteCheck,
      openSupplierNameModal,
      closeSupplierNameModal,
      selectLocation,
      selectedMaterials,
      toggleMaterialSelect,
      toggleSelectAllMaterials,
      loadMoreMaterials,
      isMaterialSelected,
      getStatusClass,
      getStatusText,
      formatDate,
      formatQty,
      handleBack,
      handleLocationScan,
    };
  },
};
</script>

<style scoped>
.location-stock-check-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica,
    Arial, sans-serif;
  background-color: var(--background-color);
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 20px;
  background-color: var(--header-background);
  color: var(--header-text);
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  height: 60px;
  box-sizing: border-box;
}

.back-btn {
  background: none;
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
  padding: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.icon-back {
  width: 24px;
  height: 24px;
  object-fit: contain;
}

.title {
  font-size: 20px;
  font-weight: 600;
  margin: 0;
  flex: 1;
  text-align: center;
}

.add-btn {
  background-color: rgba(255, 255, 255, 0.2);
  color: white;
  border: none;
  border-radius: 4px;
  padding: 8px 8px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.3s;
}

.add-btn:hover {
  background-color: rgba(255, 255, 255, 0.3);
}

.content {
  flex: 1;
  padding: 12px;
  padding-top: 32px;
  background-color: var(--background-color);
}

.form-section {
  background-color: var(--card-background);
  border-radius: 12px;
  padding: 14px;
  margin-bottom: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.top-filter {
  display: flex;
  background-color: rgba(35, 177, 192, 0.05);
  border-radius: 8px;
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  margin-bottom: 12px;
  padding: 4px;
}

.top-filter .filter-item {
  flex: 1;
  text-align: center;
  padding: 10px 0;
  cursor: pointer;
  border-radius: 6px;
  transition: all 0.2s;
  font-size: 14px;
  min-height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  min-width: 70px;
  color: var(--text-secondary);
}

.top-filter .filter-item.active {
  background-color: var(--primary-color);
  color: white;
  font-weight: 500;
}

.status-banner {
  padding: 10px 15px;
  border-radius: 6px;
  margin-bottom: 15px;
  font-size: 14px;
}

.status-banner.success {
  background-color: rgba(35, 177, 192, 0.12);
  color: var(--primary-color);
}

.status-banner.danger,
.status-banner.error {
  background-color: rgba(239, 68, 68, 0.12);
  color: var(--error-color);
}

.status-banner.warn {
  background-color: rgba(245, 158, 11, 0.12);
  color: var(--warning-color);
}

.status-banner.info {
  background-color: rgba(35, 177, 192, 0.12);
  color: var(--primary-color);
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
}

.loading-state.small-loading {
  padding: 10px 0;
  justify-content: center;
}

.loading-spinner {
  width: 30px;
  height: 30px;
  border: 3px solid rgba(35, 177, 192, 0.2);
  border-top: 3px solid var(--primary-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 15px;
}

.small-loading .loading-spinner {
  width: 20px;
  height: 20px;
  margin-bottom: 0;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.loading-text {
  color: var(--text-secondary);
  font-size: 14px;
}

.error-message {
  color: var(--error-color);
  padding: 10px;
  background-color: rgba(239, 68, 68, 0.12);
  border-radius: 6px;
  margin-bottom: 15px;
  font-size: 14px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  text-align: center;
}

.empty-icon {
  font-size: 64px;
  margin-bottom: 20px;
}

.empty-text {
  color: var(--text-secondary);
  font-size: 16px;
  margin-bottom: 20px;
}

.empty-action-btn {
  background-color: var(--primary-color);
  color: white;
  border: none;
  border-radius: 6px;
  padding: 10px 24px;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.empty-action-btn:hover {
  background-color: rgba(35, 177, 192, 0.8);
}

.card-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.data-card {
  background-color: var(--card-background);
  border-radius: 12px;
  padding: 14px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
  position: relative;
  overflow: hidden;
  border: 1px solid var(--border-color);
}

.data-card::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  background: var(--border-color);
}

.data-card.status-pending::before {
  background: var(--primary-color);
}

.data-card.status-completed::before {
  background: var(--success-color);
}

.data-card.status-canceled::before {
  background: var(--error-color);
}

.data-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid var(--border-color);
}

.card-label {
  font-weight: 600;
  color: var(--text-primary);
  font-size: 16px;
}

.card-status {
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.card-status.status-pending {
  background-color: rgba(35, 177, 192, 0.12);
  color: var(--primary-color);
}

.card-status.status-completed {
  background-color: rgba(16, 185, 129, 0.12);
  color: var(--success-color);
}

.card-status.status-canceled {
  background-color: rgba(239, 68, 68, 0.12);
  color: var(--error-color);
}

.card-body {
  margin-bottom: 15px;
}

.card-row {
  display: flex;
  margin-bottom: 8px;
  font-size: 14px;
  gap: 10px;
}

.card-row:last-child {
  margin-bottom: 0;
}

.row-label {
  color: var(--text-secondary);
  min-width: 84px;
}

.row-value {
  color: var(--text-primary);
  flex: 1;
  text-align: right;
  word-break: break-all;
}

.supplier-value {
  text-align: left;
  word-break: normal;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  display: block;
}

.supplier-value.clickable {
  cursor: pointer;
}

.card-footer {
  padding-top: 10px;
  display: flex;
  gap: 10px;
}

.enter-check-btn {
  background-color: var(--primary-color);
  color: white;
  border: none;
  border-radius: 6px;
  flex: 1;
  padding: 10px 0;
  font-size: 14px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.enter-check-btn:hover:not(:disabled) {
  background-color: rgba(35, 177, 192, 0.8);
}

.enter-check-btn:disabled {
  background-color: var(--border-color);
  cursor: not-allowed;
}

.delete-btn {
  flex: 0 0 92px;
  background-color: rgba(239, 68, 68, 0.12);
  color: var(--error-color);
  border: 1px solid var(--error-color);
  border-radius: 6px;
  white-space: nowrap;
}

.delete-btn:hover:not(:disabled) {
  background-color: rgba(239, 68, 68, 0.2);
}

.delete-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.pagination-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 0;
  margin-top: 10px;
}

.pagination-info {
  font-size: 14px;
  color: var(--text-secondary);
}

.pagination-controls {
  display: flex;
  gap: 10px;
}

.page-btn {
  padding: 8px 16px;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  background: var(--card-background);
  color: var(--text-primary);
  font-size: 14px;
  cursor: pointer;
}

.page-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-container {
  background-color: var(--card-background);
  border-radius: 12px;
  width: 90%;
  max-width: 500px;
  max-height: 80vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid var(--border-color);
}

.modal-title {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.modal-close {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: var(--text-secondary);
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-body {
  padding: 20px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group:last-child {
  margin-bottom: 0;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: var(--text-primary);
  font-size: 14px;
}

.search-input-wrapper {
  margin-bottom: 12px;
}

.search-input {
  width: 100%;
  padding: 12px 15px;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  font-size: 15px;
  box-sizing: border-box;
  height: 42px;
  transition: border-color 0.2s, box-shadow 0.2s;
  background-color: var(--card-background);
  color: var(--text-primary);
}

.search-input:focus {
  border-color: var(--primary-color);
  outline: none;
  box-shadow: 0 0 0 2px rgba(35, 177, 192, 0.2);
}

.location-info-card {
  background-color: rgba(35, 177, 192, 0.08);
  border: 1px solid rgba(35, 177, 192, 0.3);
  border-radius: 8px;
  padding: 14px;
  margin-bottom: 20px;
}

.location-info-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.location-name {
  font-weight: 600;
  font-size: 16px;
  color: var(--text-primary);
}

.location-area {
  font-size: 14px;
  color: var(--text-secondary);
}

.material-list {
  max-height: 250px;
  overflow-y: auto;
  border: 1px solid var(--border-color);
  border-radius: 6px;
}

.material-list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.select-all-btn {
  padding: 4px 12px;
  font-size: 12px;
  color: var(--primary-color);
  border: 1px solid var(--primary-color);
  border-radius: 4px;
  background-color: transparent;
  cursor: pointer;
  transition: all 0.2s;
}

.select-all-btn:hover {
  background-color: rgba(35, 177, 192, 0.08);
}

.material-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 15px;
  border-bottom: 1px solid var(--border-color);
  cursor: pointer;
  transition: background-color 0.2s;
}

.material-item:hover {
  background-color: rgba(35, 177, 192, 0.05);
}

.material-item.selected {
  background-color: rgba(35, 177, 192, 0.1);
}

.material-item:last-child {
  border-bottom: none;
}

.material-checkbox {
  margin-right: 12px;
}

.checkbox {
  width: 18px;
  height: 18px;
  border: 2px solid var(--border-color);
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
}

.checkbox.checked {
  background-color: var(--primary-color);
  border-color: var(--primary-color);
  color: white;
  font-size: 12px;
}

.material-info {
  flex: 1;
}

.material-no {
  font-weight: 500;
  color: var(--text-primary);
  font-size: 14px;
  margin-bottom: 4px;
}

.material-name {
  color: var(--text-secondary);
  font-size: 12px;
  margin-bottom: 2px;
}

.material-supplier {
  color: var(--text-secondary);
  font-size: 12px;
}

.material-qty {
  font-weight: 600;
  color: var(--primary-color);
  font-size: 14px;
}

.material-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
}

.material-status-badge {
  padding: 2px 8px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
}

.material-status-badge.status-good {
  background-color: rgba(35, 177, 192, 0.12);
  color: var(--primary-color);
}

.material-status-badge.status-bad {
  background-color: rgba(239, 68, 68, 0.12);
  color: var(--error-color);
}

.empty-list-tip {
  padding: 20px;
  text-align: center;
  color: var(--text-secondary);
  font-size: 14px;
}

.list-search {
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid var(--border-color);
  border-radius: 10px;
  background: var(--card-background);
  padding: 10px 12px;
  margin-bottom: 12px;
}

.list-search-input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 14px;
  color: var(--text-primary);
  background: transparent;
}

.list-search-clear {
  border: none;
  background: rgba(35, 177, 192, 0.12);
  color: var(--text-secondary);
  width: 24px;
  height: 24px;
  border-radius: 12px;
  line-height: 24px;
  text-align: center;
  padding: 0;
  cursor: pointer;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 20px;
  border-top: 1px solid var(--border-color);
}

.btn {
  padding: 8px 20px;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s;
}

.modal-cancel {
  background-color: var(--card-background);
  border: 1px solid var(--border-color);
  color: var(--text-primary);
}

.modal-cancel:hover {
  border-color: var(--primary-color);
  color: var(--primary-color);
}

.modal-confirm {
  background-color: var(--primary-color);
  border: none;
  color: white;
}

.modal-confirm:hover:not(:disabled) {
  background-color: rgba(35, 177, 192, 0.8);
}

.modal-confirm:disabled {
  background-color: var(--border-color);
  cursor: not-allowed;
}

.supplier-name-modal-container {
  max-width: 420px;
}

.supplier-name-modal-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.supplier-name-modal-item {
  padding: 10px 12px;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  color: var(--text-primary);
  font-size: 14px;
  line-height: 1.4;
}

.location-dropdown {
  border: 1px solid var(--border-color);
  border-radius: 6px;
  margin-top: 10px;
  max-height: 200px;
  overflow-y: auto;
}

.location-dropdown-item {
  padding: 10px 15px;
  cursor: pointer;
  border-bottom: 1px solid var(--border-color);
  font-size: 14px;
  color: var(--text-primary);
  transition: background-color 0.2s;
}

.location-dropdown-item:last-child {
  border-bottom: none;
}

.location-dropdown-item:hover {
  background-color: rgba(35, 177, 192, 0.08);
}

.location-info-card {
  background-color: rgba(35, 177, 192, 0.08);
  border-radius: 8px;
  padding: 15px;
  margin-bottom: 15px;
}

.location-info-header {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.location-name {
  font-weight: 600;
  font-size: 16px;
  color: var(--text-primary);
}

.location-area {
  font-size: 13px;
  color: var(--text-secondary);
}

.load-more-container {
  margin-top: 10px;
  text-align: center;
}

.load-more-btn {
  padding: 8px 20px;
  font-size: 14px;
  color: var(--primary-color);
  border: 1px solid var(--primary-color);
  border-radius: 6px;
  background-color: transparent;
  cursor: pointer;
  transition: all 0.2s;
}

.load-more-btn:hover:not(:disabled) {
  background-color: rgba(35, 177, 192, 0.08);
}

.load-more-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.selected-count {
  margin-top: 10px;
  padding: 10px 15px;
  background-color: rgba(35, 177, 192, 0.08);
  border-radius: 6px;
  font-size: 13px;
  color: var(--primary-color);
  text-align: center;
}
</style>
